<script setup>
import { ref, watch } from 'vue';
import { convertToDisplayDate, formatNumber } from '../js/common';
import { computed } from 'vue';
import { buttonSizeConstants } from '../js/constants';

//#region form variables
const props = defineProps({
    visible: Boolean,
    masterId: [String, Number],
    detailId: [String, Number],
    voucherType: [String, Number],
    voucherApprovalStage: [String, Number],
    voucherDetail: Array,
    updateVoucherApprovalStageViaPopup: Function
});
const emit = defineEmits(['update:visible']);
const localVisible = ref(props.visible);
const first_row = computed(() => props.voucherDetail?.[0] ?? null);
const voucher_no = computed(() => first_row.value?.no ?? null);
const voucher_date = computed(() => first_row.value?.vdate ?? null);
const voucher_party_name = computed(() => first_row.value?.party_name ?? null);
const voucher_main_account_name = computed(() => first_row.value?.main_account_name ?? null);
const voucher_master_id = computed(() => props.masterId ?? null);
const voucher_type = computed(() => props.voucherType ?? null);
const voucher_approval_stage = computed(() => props.voucherApprovalStage ?? null);
const voucher_name = computed(() => first_row.value?.voucher_name ?? null);
const voucher_category = computed(() => first_row.value?.voucher_category ?? null);
//#endregion

//#region getSet variables
watch(
    () => props.visible,
    (val) => {
        localVisible.value = val;
    }
);

watch(localVisible, (val) => {
    emit('update:visible', val);
});

const rowClass = (rowData) => {
    return Number(rowData.id) === Number(props.detailId) ? 'row-success' : '';
};
//#endregion

//#region calculations
const totalQty = computed(() => (props.voucherDetail || []).reduce((sum, item) => sum + (Number(item.qty) || 0), 0));
const totalAmount = computed(() => (props.voucherDetail || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0));
const totalNetAmount = computed(() => (props.voucherDetail || []).reduce((sum, item) => sum + (Number(item.net_amount) || 0), 0));
const totalDebit = computed(() => (props.voucherDetail || []).reduce((sum, item) => sum + (Number(item.debit) || 0), 0));
const totalCredit = computed(() => (props.voucherDetail || []).reduce((sum, item) => sum + (Number(item.credit) || 0), 0));
//#endregion
</script>

<template>
    <Dialog v-model:visible="localVisible" position="top" :modal="true" :draggable="false" :closable="false" :style="{ width: '65%' }">
        <Toolbar class="mb-4">
            <template #start>
                <label class="text-2xl font-semibold select-none whitespace-nowrap">
                    <Button icon="pi pi-file" class="mr-2 p-2" rounded />
                    Voucher Summary
                </label>
            </template>
            <template #end>
                <div class="flex flex-row items-end gap-2 justify-start md:justify-end">
                    <div class="flex flex-col items-center" v-if="voucherApprovalStage">
                        <Button icon="pi pi-check" :size="buttonSizeConstants.size" v-tooltip.bottom="'Approved'" @click="updateVoucherApprovalStageViaPopup(voucher_master_id, voucher_type, voucher_approval_stage, voucher_no)" />
                    </div>
                    <div class="flex flex-col items-center" v-if="voucherApprovalStage">
                        <Button icon="pi pi-trash" severity="warn" :size="buttonSizeConstants.size" v-tooltip.bottom="'Marked Deleted'" @click="updateVoucherApprovalStageViaPopup(voucher_master_id, voucher_type, 0, voucher_no)" />
                    </div>
                    <div class="flex flex-col items-center">
                        <Button icon="pi pi-times" severity="danger" @click="localVisible = false" :size="buttonSizeConstants.size" v-tooltip.bottom="'Close'" />
                    </div>
                </div>
            </template>
        </Toolbar>
        <div class="flex flex-wrap items-center gap-x-8 gap-y-1 rounded-md border border-gray-200 px-4 py-2 text-lg mb-4">
            <div class="flex items-center gap-2">
                <i class="pi pi-book"></i>
                <span class="font-medium">Voucher Title:</span>
                <span class="font-semibold"> {{ voucher_name }} - {{ voucher_type }}</span>
            </div>
            <div class="flex items-center gap-2">
                <i class="pi pi-tag"></i>
                <span class="font-medium">Voucher No :</span>
                <span class="font-semibold">{{ voucher_no }}</span>
            </div>
            <div class="flex items-center gap-2">
                <i class="pi pi-calendar"></i>
                <span class="font-medium">Voucher Date:</span>
                <span class="font-semibold"> {{ convertToDisplayDate(voucher_date) }} </span>
            </div>
            <div v-if="voucher_name != 'Opening Journal Voucher' && voucher_name != 'Journal Voucher' && voucher_name != 'Cash Voucher' && (voucher_main_account_name || voucher_party_name)" class="flex items-center gap-2">
                <i class="pi pi-user"></i>
                <span class="font-medium">Main Account/Party Name:</span>
                <span class="font-semibold"> {{ voucher_main_account_name }}{{ voucher_party_name }}</span>
            </div>
        </div>
        <DataTable
            v-if="voucher_category == 'Financial' && voucher_name != 'Opening Journal Voucher' && voucher_name != 'Journal Voucher' && voucher_name != 'Cash Voucher'"
            :value="voucherDetail ?? []"
            :rowClass="rowClass"
            showGridlines
            scrollable
            scrollHeight="400px"
            :rowHover="true"
        >
            <Column header="Sr No." style="width: 7%">
                <template #body="{ index }">
                    {{ index + 1 }}
                </template>
            </Column>
            <Column field="account_name" header="Name" />
            <Column field="description" header="Description" />
            <Column field="amount" header="Amount" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.amount) }}</div>
                </template>
                <template #footer>
                    <div class="text-right font-bold">{{ formatNumber(totalAmount) }}</div>
                </template>
            </Column>
        </DataTable>
        <DataTable
            v-else-if="voucher_name == 'Opening Journal Voucher' || voucher_name == 'Journal Voucher' || voucher_name == 'Cash Voucher'"
            :value="voucherDetail"
            :rowClass="rowClass"
            showGridlines
            scrollable
            scrollHeight="400px"
            :rowHover="true"
        >
            <Column header="Sr No." style="width: 7%">
                <template #body="{ index }">
                    {{ index + 1 }}
                </template>
            </Column>
            <Column field="account_name" header="Name" />
            <Column field="description" header="Description" />
            <Column field="debit" header="Debit" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.debit) }}</div>
                </template>
                <template #footer>
                    <div class="text-right font-bold">{{ formatNumber(totalDebit) }}</div>
                </template>
            </Column>

            <Column field="credit" header="Credit" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.credit) }}</div>
                </template>
                <template #footer>
                    <div class="text-right font-bold">{{ formatNumber(totalCredit) }}</div>
                </template>
            </Column>
        </DataTable>
        <DataTable v-else :value="voucherDetail" :rowClass="rowClass" showGridlines scrollable scrollHeight="400px" :rowHover="true">
            <Column header="Sr No." style="width: 7%">
                <template #body="{ index }">
                    {{ index + 1 }}
                </template>
            </Column>
            <Column style="width: 40%" field="product_name" header="Name" />
            <Column field="qty" header="Qty" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.qty) }}</div>
                </template>
                <template #footer>
                    <div class="text-right font-bold">{{ formatNumber(totalQty) }}</div>
                </template>
            </Column>
            <Column v-if="voucher_category != 'Inventory'" field="rate" header="Rate" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.rate) }}</div>
                </template>
            </Column>
            <Column v-if="voucher_category != 'Inventory'" field="amount" header="Amount" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.amount) }}</div>
                </template>
                <template #footer>
                    <div class="text-right font-bold">{{ formatNumber(totalAmount) }}</div>
                </template>
            </Column>
            <Column v-if="voucher_category != 'Inventory'" field="discount_percentage" header="Discount %" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.discount_percentage) }}</div>
                </template>
            </Column>
            <Column v-if="voucher_category != 'Inventory'" field="discount_amount" header="Discount Amount" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.discount_amount) }}</div>
                </template>
            </Column>
            <Column v-if="voucher_category != 'Inventory'" field="net_amount" header="Net Amount" class="text-right">
                <template #body="{ data }">
                    <div class="text-right">{{ formatNumber(data.net_amount) }}</div>
                </template>
                <template #footer>
                    <div class="text-right font-bold">{{ formatNumber(totalNetAmount) }}</div>
                </template>
            </Column>
        </DataTable>
    </Dialog>
</template>

<style>
.p-datatable .row-success > td {
    background-color: var(--success-color, #d5ffe9) !important;
    color: var(--success-color-text, #065f46) !important;
}
</style>
